import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BillingInfoService } from '@app/@shared/services/billing-info.service';
import { ThemingService } from '@app/@shared/services/theming.service';
import { BillingStoreService } from '@app/home/store/billing-store.service';
import { BillingStore } from '@app/interfaces/billing-store.interface';
import { PolicyData } from '@app/interfaces/policy.interface';
import { QuotePreparationCommand, QuotePreparationService } from './quote-preparation.service';
import {
  AptDialogModule,
  AptFeatureFlagService,
  AptLoadersModule,
  AptTemplatesModule,
} from '@archinsurance-da/arch-portal-tools';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'quote-preparation',
  standalone: true,
  imports: [CommonModule, AptDialogModule, AptLoadersModule, AptTemplatesModule],
  templateUrl: './quote-preparation.component.html',
  styleUrls: ['./quote-preparation.component.scss'],
})
export class QuotePreparationComponent implements OnInit, OnDestroy, OnChanges {
  private statusPollTimer: ReturnType<typeof setTimeout> | null = null;
  private destroy$: Subject<void> = new Subject<void>();
  private isStatusPollInFlight: boolean = false;
  private activePollForStatus: boolean;
  private activePollDelayMs: number;
  private activeTargetQuoteStatus: string;
  private activeUseLegacyRefreshWhenFlagOff: boolean = true;
  private activeImmediateCheckOnStart: boolean = false;
  preparingTitle: string = 'Preparing Quote';
  proceedPreQTDUntilBind: boolean = false;
  @Input() agentCode: string | number;
  @Input() policyStore: BillingStore;
  @Input() pollForStatus: boolean = true;
  @Input() pollDelayMs: number = 10000;
  @Input() targetQuoteStatus: string = 'QTD';
  @Input() consumerFlow: boolean = false;

  emptyContactMsg: string = '';

  constructor(
    private billingInfoService: BillingInfoService,
    private billingStoreService: BillingStoreService,
    private themeService: ThemingService,
    private ffService: AptFeatureFlagService,
    private quotePreparationService: QuotePreparationService
  ) {
    this.activePollForStatus = this.pollForStatus;
    this.activePollDelayMs = this.pollDelayMs;
    this.activeTargetQuoteStatus = this.targetQuoteStatus;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.policyStore) {
      this.syncQuoteStatusFromPolicyStore();
    }

    if (changes.pollForStatus && !changes.pollForStatus.firstChange) {
      this.activePollForStatus = this.pollForStatus;
    }

    if (changes.pollDelayMs && !changes.pollDelayMs.firstChange) {
      this.activePollDelayMs = this.pollDelayMs;
    }

    if (changes.targetQuoteStatus && !changes.targetQuoteStatus.firstChange) {
      this.activeTargetQuoteStatus = this.targetQuoteStatus;
    }
  }

  ngOnInit(): void {
    this.activePollForStatus = this.pollForStatus;
    this.activePollDelayMs = this.pollDelayMs;
    this.activeTargetQuoteStatus = this.targetQuoteStatus;
    this.syncQuoteStatusFromPolicyStore();

    this.ffService.flagChange.pipe(takeUntil(this.destroy$)).subscribe((flags: any) => {
      const proceedPreQTDUntilBind = !flags.proceedPreQTDUntilBind?.current
        ? flags.proceedPreQTDUntilBind
        : flags.proceedPreQTDUntilBind.current;
      this.proceedPreQTDUntilBind = proceedPreQTDUntilBind;
    });

    this.quotePreparationService.preparingCommands$.pipe(takeUntil(this.destroy$)).subscribe((command) => {
      if (command.action === 'start') {
        this.setIPGPreparing(command.title, command);
      }

      if (command.action === 'stop') {
        this.stopIPGPreparing();
      }
    });

    this.quotePreparationService.currentQuoteStatus$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      this.currentQuoteStatus = status;
    });
  }

  ngOnDestroy(): void {
    this.clearStatusPollTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  stopIPGPreparing(): void {
    this.clearStatusPollTimer();
    this.isPreparingQuote = false;
    this.prepareQuoteTimedOut = false;
  }

  setIPGPreparing(title: string = 'Preparing Quote', command?: QuotePreparationCommand): void {
    this.preparingTitle = title;
    this.preparingError = undefined;
    this.prepareQuoteTimedOut = false;
    this.isPreparingQuote = true;

    this.activePollForStatus = command?.pollForStatus ?? this.pollForStatus;
    this.activePollDelayMs = command?.pollDelayMs ?? this.pollDelayMs;
    this.activeTargetQuoteStatus = command?.targetQuoteStatus ?? this.targetQuoteStatus;
    this.activeUseLegacyRefreshWhenFlagOff = command?.useLegacyRefreshWhenFlagOff ?? true;
    this.activeImmediateCheckOnStart = command?.immediateCheckOnStart ?? false;

    this.clearStatusPollTimer();

    if (this.activePollForStatus) {
      if (this.activeImmediateCheckOnStart && !this.shouldRefreshPageForLegacyFlow()) {
        this.pollQuoteStatusNow();
        return;
      }

      this.scheduleProgressCheck(this.activePollDelayMs);
    }
  }

  refreshOrCheckNow(): void {
    if (this.shouldRefreshPageForLegacyFlow()) {
      this.refreshPage();
      return;
    }

    this.pollQuoteStatusNow();
  }

  preparingMessages: string[] = [
    'Your quote is being prepared.',
    'Your quote will be ready shortly. Thank you for your patience.',
  ];
  isPreparingQuote: boolean = false;
  prepareQuoteTimedOut: boolean = false;
  preparingError: string;
  currentQuoteStatus: string | null = null;

  prepareTimeOutChange($event: any) {
    console.log('preparing quote timed out $event', $event);
    this.prepareQuoteTimedOut = $event;
    this.pollQuoteStatusNow();
  }

  quoteReadyForEditOrProceed(): boolean {
    return this.quotePreparationService.quoteReadyForEditOrProceed(this.policyStore, this.proceedPreQTDUntilBind);
  }

  hasTargetQuoteStatus(): boolean {
    return this.quotePreparationService.isQuoteInStatus(this.policyStore, this.activeTargetQuoteStatus);
  }

  getCurrentQuoteStatus(): string | null {
    return this.quotePreparationService.getCurrentQuoteStatus();
  }

  private scheduleProgressCheck(delayMs: number): void {
    this.clearStatusPollTimer();
    this.statusPollTimer = setTimeout(() => {
      if (this.shouldRefreshPageForLegacyFlow()) {
        this.refreshPage();
        return;
      }

      this.pollQuoteStatusNow();
    }, delayMs);
  }

  private pollQuoteStatusNow(): void {
    if (!this.isPreparingQuote || this.isStatusPollInFlight) {
      return;
    }

    const policyId = this.policyStore?.id;
    const product = this.policyStore?.product;

    if (!policyId || !product) {
      this.preparingError = 'Unable to check quote status right now.';
      console.log('Unable to check quote status: policyId or product is missing', { policyId, product });
      return;
    }

    this.isStatusPollInFlight = true;
    this.billingInfoService
      .getPolicy(policyId, product, this.consumerFlow)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (policyData: PolicyData) => {
          this.isStatusPollInFlight = false;
          this.updatePolicyStore(policyData);

          if (this.hasTargetQuoteStatus()) {
            this.stopIPGPreparing();
            return;
          }

          if (this.isPreparingQuote && this.activePollForStatus) {
            this.scheduleProgressCheck(this.activePollDelayMs);
          }
        },
        () => {
          this.isStatusPollInFlight = false;
          this.preparingError = 'Unable to check quote status right now.';

          if (this.isPreparingQuote && this.activePollForStatus) {
            this.scheduleProgressCheck(this.activePollDelayMs);
          }
        }
      );
  }

  private shouldRefreshPageForLegacyFlow(): boolean {
    return !this.proceedPreQTDUntilBind && this.activeUseLegacyRefreshWhenFlagOff;
  }

  private refreshPage(): void {
    location.reload();
  }

  private updatePolicyStore(policyData: PolicyData): void {
    this.policyStore = {
      ...(this.policyStore || {}),
      id: policyData?.policy?.guid || policyData?.policy?.policyId || this.policyStore?.id,
      product: this.policyStore?.product,
      policyData,
    };

    this.quotePreparationService.setCurrentQuoteStatus(policyData?.policy?.quoteStatus);
    this.billingStoreService.setBillingStore(this.policyStore);
  }

  private clearStatusPollTimer(): void {
    if (this.statusPollTimer) {
      clearTimeout(this.statusPollTimer);
      this.statusPollTimer = null;
    }
  }

  private syncQuoteStatusFromPolicyStore(): void {
    this.quotePreparationService.setCurrentQuoteStatus(this.quotePreparationService.getQuoteStatus(this.policyStore));
  }
}
